import React, { FC, FormHTMLAttributes, useCallback, useMemo } from 'react';
import { FORM_ERROR } from 'final-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Form } from 'react-final-form';
import { useDispatchRequest, useQuery } from '@redux-requests/react';

import { t, extractFromHtml, getInitialsFromString } from 'utils';
import routesByName from 'constants/routes';
import { AppsConstants } from '../store/constants';
import { changeDeveloperAppStatus } from 'modules/userApps/store/actions/index';
import { changeAppInfo, deleteApp, getApp } from 'modules/userApps/store/actions';
import { AddNewScopeModal, EditPlanModal } from 'components/modals';
import { AppStatus, DeveloperAppVerb, ITooltipFields, appStatusButtonOptions } from './constants';
import {
  toggleEditPlanModal,
  toggleAddNewScopesModal,
  toggleAuthoriseAccounts,
} from 'modules/layout/store/actions';

import { supportOptions } from '../AppDistribution/constants';
import AppSettingsForm from './AppSettingsForm';
import { AppIconType } from './components/AppIcon/AppIcon';

export interface IAppSettings extends FormHTMLAttributes<HTMLFormElement> {
  id: string;
}

const AppSettings: FC<IAppSettings> = ({ id }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const dispatchRequest = useDispatchRequest();
  const {
    data: {
      plan,
      name,
      roles,
      description,
      installmentsCount,
      enterprises,
      companies,
      iconType,
      isPublic,
      status,
      hookUrl,
      hookSecret,
      iconImageUrl,
      iconTextColor,
      abbreviatedName,
      allowFullscreenView,
      iconBackgroundColor,
      iconFontAwesomeName,
    },
  } = useQuery({ type: AppsConstants.APP, requestKey: id });
  const appPlan = supportOptions.find((planOption) => planOption.value === plan)?.label;
  const appPlanLabel = t(appPlan);
  const companiesAndEnterprises = [...companies, ...enterprises];
  const tooltipData = useMemo<ITooltipFields>(() => appStatusButtonOptions[status], [status]);
  const isFormDisabled = status !== AppStatus.DRAFT;
  const authoizePermissionsPublic =
    status === AppStatus.DRAFT && companiesAndEnterprises.length === 0;
  const authoizePermissionsPrivate =
    (status === AppStatus.DRAFT && companiesAndEnterprises.length === 0) ||
    status === AppStatus.PUBLISHED;
  const isAuthoriseAccountsEnabled = isPublic
    ? authoizePermissionsPublic
    : authoizePermissionsPrivate;
  // hook for notifying when app is installed
  const isCustomProdUrlEnabled = !!hookSecret || !!hookUrl;

  const handleAuthoriseModalOpen = useCallback(() => {
    dispatch(toggleAuthoriseAccounts(true));
  }, [dispatch]);

  const handleAddNewScopeModalOpen = useCallback(() => {
    dispatch(toggleAddNewScopesModal(true));
  }, [dispatch]);

  const handleEditPlanModalOpen = useCallback(() => {
    dispatch(toggleEditPlanModal(true));
  }, [dispatch]);

  const handleUpdateApp = useCallback(
    async (values) => {
      const {
        hookSecret,
        hookUrl,
        isCustomProdUrlEnabled,
        abbreviatedName,
        iconType,
        iconBackgroundColor,
        iconFontAwesomeName,
        iconImageUrl,
        iconTextColor,
      } = values;
      const { description } = values;
      const plainTextFromDescription = extractFromHtml(description);
      const errors: { [key: string]: string } = {};
      if (plainTextFromDescription.length > 400) {
        errors.description = t('errors.length.invalid');
      }
      // if (iconType === AppIconType.FONTAWESOME) {
      //   errors.abbreviatedName = t('errors.required'); //TODO For now will be without validation
      // }
      if (isCustomProdUrlEnabled) {
        if (!hookUrl.length) errors.hookUrl = t('errors.hookUrl.mandatory');
        if (!hookSecret.length) errors.hookSecret = t('errors.hookSecret.mandatory');
      }
      if (Object.keys(errors).length > 0) return errors;
      const transformedValues = {
        ...values,
        iconBackgroundColor: iconType !== AppIconType.IMAGE ? iconBackgroundColor : '',
        iconTextColor: iconType !== AppIconType.IMAGE ? iconTextColor : '',
        iconFontAwesomeName: iconType === AppIconType.FONTAWESOME ? iconFontAwesomeName : '',
        iconImageUrl: iconType === AppIconType.IMAGE ? iconImageUrl : null,
        abbreviatedName: iconType === AppIconType.DEFAULT ? abbreviatedName : '',
        hookSecret: isCustomProdUrlEnabled ? hookSecret : null,
        hookUrl: isCustomProdUrlEnabled ? hookUrl : null,
      };
      const { error } = await dispatchRequest(changeAppInfo({ ...transformedValues, id }));
      if (error?.data?.errors?.length) {
        return { [FORM_ERROR]: error?.data?.errors[0] };
      } else {
        await dispatchRequest(getApp(id));
      }
    },
    [dispatchRequest, id]
  );

  const handleRemove = useCallback(async () => {
    let data = null;
    const { PUBLISHED, DRAFT, PENDING } = AppStatus;
    switch (status) {
      // case DECLINED: {
      //   ({ data } = await dispatchRequest(
      //     changeDeveloperAppStatus(id, DeveloperAppVerb.REQUEST_APPROVAL) // go back to PENDING
      //   ));
      //   break;
      // }
      case PENDING: {
        ({ data } = await dispatchRequest(
          changeDeveloperAppStatus(id, DeveloperAppVerb.CANCEL_APPROVAL) // go back to DRAFT
        ));
        break;
      }
      case PUBLISHED: {
        ({ data } = await dispatchRequest(
          changeDeveloperAppStatus(id, DeveloperAppVerb.UNPUBLISH) // go back to APPROVED
        ));
        break;
      }
      case DRAFT: {
        ({ data } = await dispatchRequest(deleteApp(id))); // delete the app
        break;
      }
    }
    if (data) {
      if (status === DRAFT) {
        // if app is deleted redirect to app list
        history.push(routesByName.apps.index);
        return;
      }
      await dispatchRequest(getApp(id)); // get app with new status
    }
    if (!data) return t('errors.somethingWentWrong');
  }, [dispatchRequest, history, id, status]);

  return (
    <>
      <Form
        onSubmit={handleUpdateApp}
        validate={(values) => {
          const { description } = values;
          const plainTextFromDescription = extractFromHtml(description);
          const errors: { [key: string]: string } = {};
          if (plainTextFromDescription.length > 400) {
            errors.description = t('errors.length.invalid');
          }
          return errors;
        }}
        initialValues={{
          description,
          isCustomProdUrlEnabled,
          hookUrl: hookUrl || '',
          hookSecret: hookSecret || '',
          allowFullscreenView: !!allowFullscreenView,
          iconType: iconType || 'DEFAULT',
          iconBackgroundColor: iconBackgroundColor || '',
          iconTextColor: iconTextColor || '',
          iconImageUrl: iconImageUrl || '',
          iconFontAwesomeName: iconFontAwesomeName || '',
          abbreviatedName: abbreviatedName || getInitialsFromString(name || '', 3),
        }}
        render={(props) => (
          <AppSettingsForm
            id={id}
            roles={roles}
            status={status}
            iconType={iconType}
            tooltipData={tooltipData}
            selectedPlan={appPlanLabel}
            handleRemove={handleRemove}
            iconImageUrl={iconImageUrl}
            iconTextColor={iconTextColor}
            isFormDisabled={isFormDisabled}
            abbreviatedName={abbreviatedName}
            installmentsCount={installmentsCount}
            iconBackgroundColor={iconBackgroundColor}
            iconFontAwesomeName={iconFontAwesomeName}
            allowFullscreenView={allowFullscreenView}
            companiesAndEnterprises={companiesAndEnterprises}
            handleEditPlanModalOpen={handleEditPlanModalOpen}
            handleAuthoriseModalOpen={handleAuthoriseModalOpen}
            isAuthoriseAccountsEnabled={isAuthoriseAccountsEnabled}
            handleAddNewScopeModalOpen={handleAddNewScopeModalOpen}
            {...props}
          />
        )}
      />
      <AddNewScopeModal />
      <EditPlanModal />
    </>
  );
};

export default AppSettings;
